// @flow

import React, { useState } from "react";
import { observer } from "mobx-react";
import { Button, Classes, ControlGroup, Dialog, FormGroup, HTMLSelect, Intent } from "@blueprintjs/core";
import styled from "@emotion/styled";
import passwordGenerator from "password-generator";

const FormGroupStyled = styled("div")`
  .bp3-form-group {
    .bp3-label {
      min-width: 150px;
    }
    .bp3-form-content {
      flex-grow: 1;
    }
  }
`;

const DialogFooter = styled("div")`
  box-shadow: 0 -1px 0 rgba(16, 22, 26, 0.15);
`;

type Props = {
  modal: Object,
  userGroups: Array<Object>,
  addUser: Function
};

function AddUserModal(props: Props) {
  const { modal, userGroups = [], addUser } = props;
  const { data: users } = modal;

  const rolesOptions = userGroups.map(group => group.name);

  const [email, setEmail] = useState("");
  const [fullName, setFullName] = useState("");
  const [role, setRole] = useState(rolesOptions[0]);
  const usersMailList = users.map(user => user.email);
  const userExists = usersMailList.includes(email);

  function submitForm(event: ?Event) {
    if (event) {
      event.preventDefault();
    }
    modal.setModal("");
    addUser({
      email,
      group: role,
      name: fullName,
      password: passwordGenerator(30, false),
      tabs: []
    });
  }

  const emailHelperText = userExists ? "Email is already in use" : "";
  const emailIntent = userExists ? Intent.DANGER : Intent.NONE;

  function onEmailChange(event) {
    const value = event.target.value || "";

    setEmail(value.toLowerCase());
  }

  return (
    <>
      <Dialog
        className="p-0"
        isOpen={modal.modalActive === "addUser"}
        onClose={() => modal.setModal("")}
        title="Invite User"
      >
        <form className="p-0" data-testid="add-user-modal" onSubmit={(event: Event) => submitForm(event)}>
          <div className={`${Classes.DIALOG_BODY} m-0 p-3`}>
            <FormGroupStyled className="d-flex flex-column w-100">
              <FormGroup className="font-weight-bold mb-4" label="Full name" labelFor="name-input">
                <input
                  autoFocus
                  className={`w-100 ${Classes.INPUT}`}
                  id="name-input"
                  onChange={e => setFullName(e.target.value)}
                  placeholder="Enter full name"
                  required
                  type="text"
                  value={fullName}
                />
              </FormGroup>
              <FormGroup
                className="font-weight-bold mb-4"
                helperText={emailHelperText}
                intent={emailIntent}
                label="Email"
                labelFor="email-input"
              >
                <input
                  className={`w-100 ${Classes.INPUT}`}
                  id="email-input"
                  onChange={onEmailChange}
                  placeholder="Enter email"
                  required
                  type="email"
                  value={email}
                />
              </FormGroup>
              <FormGroup className="font-weight-bold mb-4" label="Role" labelFor="role-input">
                <ControlGroup>
                  <HTMLSelect
                    options={rolesOptions}
                    value={role}
                    required
                    onChange={e => setRole(e.currentTarget.value)}
                  />
                </ControlGroup>
              </FormGroup>
            </FormGroupStyled>
          </div>
          <DialogFooter className={`${Classes.DIALOG_FOOTER} mx-0 px-3 py-2`}>
            <div className={Classes.DIALOG_FOOTER_ACTIONS}>
              <Button data-testid="invite-user-button" disabled={userExists} intent="primary" type="submit">
                Invite
              </Button>
            </div>
          </DialogFooter>
        </form>
      </Dialog>
    </>
  );
}

export default observer(AddUserModal);
